import type { IndicatorStyle } from 'klinecharts'
import type {
  FigureDeclaration,
  FigureStyleOverride,
  IndicatorDefinition,
  IndicatorStyleOverrides,
  PlotType,
} from './types'

/* ------------------------------------------------------------------ */
/*  Dash → klinecharts LineType conversion                              */
/* ------------------------------------------------------------------ */
/* klinecharts LineType is an enum but compiles to string values.      */
/* We work with the canonical strings 'solid' / 'dashed' and a         */
/* 'dotted' shorthand that we emit as a short dashedValue.             */

function dashStyle(style: FigureStyleOverride['style']): {
  style: 'solid' | 'dashed'
  dashedValue: number[]
} {
  if (style === 'dashed') return { style: 'dashed', dashedValue: [4, 4] }
  if (style === 'dotted') return { style: 'dashed', dashedValue: [1, 3] }
  // klinecharts' line-merge reads styles.dashedValue[0]/[1] unconditionally,
  // even for solid lines, so always carry the array (its default is [2, 2]).
  return { style: 'solid', dashedValue: [2, 2] }
}

/* ------------------------------------------------------------------ */
/*  Plot type                                                           */
/* ------------------------------------------------------------------ */

/** The plot type a figure draws as: the user's pick, else the one its type declares. */
export function plotTypeOf(figure: FigureDeclaration, ov: FigureStyleOverride): PlotType {
  if (ov.plotType) return ov.plotType
  if (figure.type === 'line') return 'line'
  if (figure.type === 'circle') return 'circles'
  return figure.barWidth === 'thin' ? 'histogram' : 'columns'
}

/**
 * What a bar or circle style needs to be drawn as a line (patched klinecharts
 * IndicatorView reads these when the plot type is `line`). Their `style` already
 * holds the fill mode, so the dash travels as `lineStyle`.
 */
function lineFields(figure: FigureDeclaration, ov: FigureStyleOverride) {
  const dash = dashStyle((ov.style ?? figure.defaultStyle) as FigureStyleOverride['style'])
  return {
    size: ov.size ?? figure.defaultSize ?? 1,
    lineStyle: dash.style,
    dashedValue: dash.dashedValue,
  }
}

/* ------------------------------------------------------------------ */
/*  Build a klinecharts IndicatorStyle from definition + overrides     */
/* ------------------------------------------------------------------ */

/**
 * Materialize the final per-indicator klinecharts style object. Starts
 * from the catalog defaults and layers user overrides on top, producing
 * the shape klinecharts expects in `overrideIndicator({ styles })`.
 *
 * Caller is responsible for handing the result to klinecharts; this
 * module deliberately has no klinecharts dependency beyond types so it
 * can run under unit tests without the full chart bootstrap.
 */
export function buildIndicatorStyles(
  def: IndicatorDefinition,
  overrides: IndicatorStyleOverrides | undefined,
): Partial<IndicatorStyle> {
  const lines: Array<Record<string, unknown>> = []
  const bars: Array<Record<string, unknown>> = []
  const circles: Array<Record<string, unknown>> = []

  for (const figure of def.figures) {
    const ov = overrides?.[figure.key] ?? {}
    if (figure.type === 'line') {
      lines.push(buildLineStyle(figure, ov))
    } else if (figure.type === 'bar') {
      bars.push(buildBarStyle(figure, ov))
    } else if (figure.type === 'circle') {
      circles.push(buildCircleStyle(figure, ov))
    }
  }

  const out: Partial<IndicatorStyle> = {}
  if (lines.length > 0) out.lines = lines as unknown as IndicatorStyle['lines']
  if (bars.length > 0) out.bars = bars as unknown as IndicatorStyle['bars']
  if (circles.length > 0) out.circles = circles as unknown as IndicatorStyle['circles']
  return out
}

function buildLineStyle(
  figure: FigureDeclaration,
  ov: FigureStyleOverride,
): Record<string, unknown> {
  const dash = dashStyle((ov.style ?? figure.defaultStyle) as FigureStyleOverride['style'])
  const hidden = ov.visible === false
  return {
    // A user-set opacity wins; otherwise fall back to the figure's declared
    // default (e.g. the volume MA at 0.5) so a fresh add renders at the right
    // alpha. A HIDDEN line renders fully transparent: klinecharts draws native
    // lines regardless of the `visible` flag below, so transparency is what
    // actually makes the line disappear for those.
    color: hidden
      ? 'rgba(0,0,0,0)'
      : applyOpacity(ov.color ?? figure.defaultColor, ov.opacity ?? figure.defaultOpacity),
    size: ov.size ?? figure.defaultSize ?? 1,
    smooth: false,
    // Passed through for custom-draw indicators (pivot, HTF) to honor per-line
    // hide; klinecharts ignores the extra field on SmoothLineStyle (which is why
    // the transparent color above is needed for its native line rendering).
    // ALWAYS emitted: klinecharts MERGES style overrides, so omitting the key
    // when visible would leave a previously-sent `false` in place forever —
    // hiding worked but showing again silently didn't.
    visible: ov.visible !== false,
    // ALWAYS emitted, like `visible`: a merged-in pick would otherwise outlive a reset.
    plotType: plotTypeOf(figure, ov),
    ...dash,
  }
}

function buildBarStyle(
  figure: FigureDeclaration,
  ov: FigureStyleOverride,
): Record<string, unknown> {
  const up = ov.upColor ?? figure.defaultUpColor ?? ov.color ?? figure.defaultColor
  const down = ov.downColor ?? figure.defaultDownColor ?? ov.color ?? figure.defaultColor
  // A user-set opacity wins; otherwise fall back to the figure's declared
  // default (e.g. volume bars at 0.5) so a fresh add renders at the right alpha.
  const opacity = ov.opacity ?? figure.defaultOpacity
  const out: Record<string, unknown> = {
    upColor: applyOpacity(up, opacity),
    downColor: applyOpacity(down, opacity),
    noChangeColor: applyOpacity(ov.color ?? figure.defaultColor, opacity),
    style: 'fill',
    borderStyle: 'solid',
    borderSize: 1,
    // Read by indicators that colour their own bars (MACD's histogram
    // callback). ALWAYS emitted: klinecharts MERGES style overrides, so an
    // omitted key would leave a previous `false` in place forever.
    visible: ov.visible !== false,
    plotType: plotTypeOf(figure, ov),
    ...lineFields(figure, ov),
  }
  const hist = figure.defaultHistogramColors
  if (hist) {
    // Extra keys ride along on bars[i] (klinecharts' merge clones arbitrary
    // keys). A legacy Rising/Falling override maps onto the two strong colours.
    out.growAboveColor = applyOpacity(ov.growAboveColor ?? ov.upColor ?? hist.growAbove, opacity)
    out.fallAboveColor = applyOpacity(ov.fallAboveColor ?? hist.fallAbove, opacity)
    out.growBelowColor = applyOpacity(ov.growBelowColor ?? hist.growBelow, opacity)
    out.fallBelowColor = applyOpacity(ov.fallBelowColor ?? ov.downColor ?? hist.fallBelow, opacity)
  }
  return out
}

function buildCircleStyle(
  figure: FigureDeclaration,
  ov: FigureStyleOverride,
): Record<string, unknown> {
  const up = ov.upColor ?? figure.defaultUpColor ?? ov.color ?? figure.defaultColor
  const down = ov.downColor ?? figure.defaultDownColor ?? ov.color ?? figure.defaultColor
  return {
    upColor: applyOpacity(up, ov.opacity),
    downColor: applyOpacity(down, ov.opacity),
    noChangeColor: applyOpacity(ov.color ?? figure.defaultColor, ov.opacity),
    style: 'fill',
    borderStyle: 'solid',
    borderSize: 1,
    plotType: plotTypeOf(figure, ov),
    ...lineFields(figure, ov),
  }
}

/**
 * Multiply hex/rgba color by opacity 0..1. Returns rgba() when opacity
 * is set and < 1; otherwise the input color unchanged. Hex shorthand
 * (#abc) is expanded; non-hex inputs pass through unchanged when
 * opacity is 1.
 */
export function applyOpacity(color: string, opacity?: number): string {
  if (opacity === undefined || opacity >= 1) return color
  if (opacity <= 0) return 'rgba(0,0,0,0)'
  const rgb = hexToRgb(color)
  if (!rgb) return color
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  if (!hex.startsWith('#')) return null
  let body = hex.slice(1)
  if (body.length === 3)
    body = body
      .split('')
      .map((c) => c + c)
      .join('')
  if (body.length !== 6) return null
  const r = parseInt(body.slice(0, 2), 16)
  const g = parseInt(body.slice(2, 4), 16)
  const b = parseInt(body.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return null
  return { r, g, b }
}
